import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import {
    CreateServiceAccountRequest,
    DeleteServiceAccountRequest,
    GetServiceAccountRequest,
    ListServiceAccountsRequest,
    UpdateServiceAccountRequest,
} from "gitpod-next-api/gitpod/v1/service_account_pb";
import type { ServiceAccount } from "gitpod-next-api/gitpod/v1/service_account_pb";
import { Timestamp } from "@bufbuild/protobuf";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { Code, ConnectError } from "@connectrpc/connect";
import { ResourceOperation, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { type GitpodAPI } from "@/api";

export type PlainServiceAccount = PlainMessage<ServiceAccount>;

const toPlainServiceAccount = (serviceAccount: ServiceAccount): PlainServiceAccount => {
    return toPlainMessage(serviceAccount);
};

export const serviceAccountsQueryKey = {
    list: () => keyWithPrincipal(["service-accounts", "list"]),
    get: (serviceAccountId: string) => keyWithPrincipal(["service-accounts", "get", serviceAccountId]),
    accessToken: (serviceAccountId: string) => keyWithPrincipal(["service-accounts", "accessToken", serviceAccountId]),
};

export const handleServiceAccountEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    switch (evt.operation) {
        case ResourceOperation.CREATE: {
            await client.invalidateQueries({ queryKey: serviceAccountsQueryKey.list() });
            break;
        }
        case ResourceOperation.UPDATE:
        case ResourceOperation.UPDATE_STATUS: {
            const serviceAccount = await refetchServiceAccount(api, evt.resourceId);
            setServiceAccountInCache(
                client,
                evt.resourceId,
                serviceAccount ? toPlainServiceAccount(serviceAccount) : undefined,
            );
            break;
        }
        case ResourceOperation.DELETE: {
            setServiceAccountInCache(client, evt.resourceId, undefined);
            break;
        }
    }
};

const refetchServiceAccount = async (api: GitpodAPI, serviceAccountId: string) => {
    try {
        const response = await api.serviceAccountService.getServiceAccount({ serviceAccountId });
        return response.serviceAccount;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch service account", serviceAccountId);
        }
    }
};

const setServiceAccountInCache = (
    client: QueryClient,
    serviceAccountId: string,
    serviceAccount: PlainServiceAccount | undefined,
) => {
    client.setQueryData(serviceAccountsQueryKey.get(serviceAccountId), serviceAccount ? serviceAccount : null);
    client.setQueryData(serviceAccountsQueryKey.list(), (currentData?: { serviceAccounts: PlainServiceAccount[] }) => {
        if (!currentData) {
            return serviceAccount ? { serviceAccounts: [serviceAccount] } : currentData;
        }

        const updatedList = currentData.serviceAccounts
            .map((sa: PlainServiceAccount) => {
                if (sa.id !== serviceAccountId) {
                    return sa;
                }
                return serviceAccount;
            })
            .filter((sa) => !!sa);

        // Add to the top of the list if it doesn't already exist
        if (serviceAccount && !currentData.serviceAccounts.some((sa) => sa.id === serviceAccountId)) {
            updatedList.unshift(serviceAccount);
        }

        return { serviceAccounts: updatedList };
    });
};

export const useListServiceAccounts = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: serviceAccountsQueryKey.list(),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const request = new ListServiceAccountsRequest({});
            const response = await api.serviceAccountService.listServiceAccounts(request);
            return {
                serviceAccounts: response.serviceAccounts.map(toPlainServiceAccount),
            };
        },
        enabled: !!user && enabled,
        retry: defaultRetry,
        throwOnError: defaultThrowOnError,
    });
};

export type useGetServiceAccountParams = {
    serviceAccountId: string;
    enabled?: boolean;
};

export const useGetServiceAccount = ({ serviceAccountId, enabled = true }: useGetServiceAccountParams) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: serviceAccountsQueryKey.get(serviceAccountId),
        queryFn: async () => {
            const request = new GetServiceAccountRequest({ serviceAccountId });
            const { serviceAccount } = await api.serviceAccountService.getServiceAccount(request);

            if (!serviceAccount) {
                throw new Error("Error fetching service account");
            }

            return toPlainServiceAccount(serviceAccount);
        },
        enabled: enabled && !!serviceAccountId,
        retry: defaultRetry,
        throwOnError: defaultThrowOnError,
    });
};

export type CreateServiceAccountParams = {
    name: string;
    description?: string;
    validUntil: Date;
};

export const useCreateServiceAccount = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ name, description, validUntil }: CreateServiceAccountParams) => {
            const request = new CreateServiceAccountRequest({
                name,
                description,
                validUntil: Timestamp.fromDate(validUntil),
            });
            const { serviceAccount } = await api.serviceAccountService.createServiceAccount(request);

            if (!serviceAccount) {
                throw new Error("Error fetching service account");
            }

            return toPlainServiceAccount(serviceAccount);
        },
        onSuccess: (newServiceAccount) => {
            setServiceAccountInCache(queryClient, newServiceAccount.id, newServiceAccount);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type UpdateServiceAccountParams = {
    serviceAccountId: string;
    name?: string;
    description?: string;
};

export const useUpdateServiceAccount = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ serviceAccountId, name, description }: UpdateServiceAccountParams) => {
            const request = new UpdateServiceAccountRequest({
                serviceAccountId,
                name,
                description,
            });
            const response = await api.serviceAccountService.updateServiceAccount(request);

            if (!response?.serviceAccount) {
                throw new Error("Failed to update service account");
            }

            return toPlainServiceAccount(response.serviceAccount);
        },
        onSuccess: (updatedServiceAccount) => {
            setServiceAccountInCache(queryClient, updatedServiceAccount.id, updatedServiceAccount);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useDeleteServiceAccount = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (serviceAccountId: string) => {
            const request = new DeleteServiceAccountRequest({ serviceAccountId });
            await api.serviceAccountService.deleteServiceAccount(request);
            return serviceAccountId;
        },
        onSuccess: (serviceAccountId) => {
            setServiceAccountInCache(queryClient, serviceAccountId, undefined);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type UseCreateServiceAccountAccessTokenParams = {
    serviceAccountId: string;
    enabled?: boolean;
};

export const useCreateServiceAccountAccessToken = ({
    serviceAccountId,
    enabled = true,
}: UseCreateServiceAccountAccessTokenParams) => {
    const api = useGitpodAPI();
    const tokenValidDuration = 1000 * 60 * 30;
    return useQuery<string, Error>({
        queryKey: serviceAccountsQueryKey.accessToken(serviceAccountId),
        queryFn: async (): Promise<string> => {
            const { token } = await api.serviceAccountService.createServiceAccountAccessToken({
                serviceAccountId,
            });
            if (!token) {
                throw new Error("No access token provided");
            }

            return token;
        },
        enabled: !!serviceAccountId && enabled,
        staleTime: tokenValidDuration,
        gcTime: tokenValidDuration,
    });
};
